import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Briefcase, Save } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import {
  SearchForm,
  emptySearchForm,
  toSearchInsertPayload,
  type SearchFormValues,
} from "@/components/clients/SearchForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBanner } from "@/components/ui/status-banner";
import { supabase } from "@/lib/supabase";
import type { Company } from "@/lib/types";

export const Route = createFileRoute("/searches/new")({
  validateSearch: (s: Record<string, unknown>) => ({
    company_id: typeof s.company_id === "string" ? s.company_id : undefined,
  }),
  component: NewSearchPage,
});

function NewSearchPage() {
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para crear búsquedas.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <NewSearchForm />
        </AppShell>
      )}
    </LoginGate>
  );
}

function NewSearchForm() {
  const { company_id } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<SearchFormValues>(emptySearchForm());
  const [error, setError] = useState<string | null>(null);

  const { data: company, isLoading: loadingCompany } = useQuery({
    queryKey: ["company", company_id],
    queryFn: async (): Promise<Company | null> => {
      if (!company_id) return null;
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", company_id)
        .single();
      if (error) throw error;
      return data as Company;
    },
    enabled: !!company_id,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!company_id) throw new Error("Falta el cliente. Volvé al cliente y crea la búsqueda desde ahí.");
      if (!form.puesto.trim()) throw new Error("Puesto requerido.");
      const payload = toSearchInsertPayload(form, company_id);
      const { data, error } = await supabase
        .from("searches")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["searches"] });
      navigate({
        to: "/searches/$searchId",
        params: { searchId: res.id },
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!company_id) {
    return (
      <div className="px-6 py-5">
        <StatusBanner variant="error" title="Falta el cliente">
          Las búsquedas se crean siempre dentro de un cliente. Volvé al cliente
          y usá el botón <strong>Nueva búsqueda</strong>.
        </StatusBanner>
        <div className="mt-4">
          <Button variant="default" asChild>
            <Link to="/clients">
              <ArrowLeft className="h-3.5 w-3.5" />
              Ir a clientes
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      {company && (
        <Link
          to="/clients/$clientId"
          params={{ clientId: company.id }}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-3 w-3" />
          {company.name}
        </Link>
      )}

      <div className="flex items-center gap-3 mb-2">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 border border-primary/30">
          <Briefcase className="h-4 w-4 text-primary" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Nueva búsqueda
        </h1>
      </div>
      {loadingCompany ? (
        <p className="text-muted-foreground text-sm mb-8">Cargando cliente…</p>
      ) : company ? (
        <p className="text-muted-foreground text-sm mb-8">
          Para <strong className="text-foreground">{company.name}</strong>
        </p>
      ) : null}

      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="No se pudo crear la búsqueda"
            onDismiss={() => setError(null)}
          >
            {error}
          </StatusBanner>
        </div>
      )}

      <Card className="p-6">
        <SearchForm value={form} onChange={setForm} />
        <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-border">
          <Button variant="ghost" asChild>
            <Link
              to="/clients/$clientId"
              params={{ clientId: company_id }}
            >
              Cancelar
            </Link>
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!form.puesto.trim()}
          >
            <Save className="h-4 w-4" />
            Crear búsqueda
          </Button>
        </div>
      </Card>
    </div>
  );
}
